/* global window */
import jwtDecode from 'jwt-decode';
import Auth from '../Auth';
import { supportLocalStorage } from '../../helpers/localStorage';

export class AuthAction {
    constructor(action) {
        this.action = action;
    }
}

export const withAuthentication = a => new AuthAction(a);

export const setTokenExpire = expire => {
    if (supportLocalStorage()) {
        window.localStorage.setItem('tokenExpire', `${ expire }`);
    }
    return {
        type: 'SET_TOKEN_EXPIRE',
        expire
    };
};

const setRefreshTokenState = data => ({
    type: 'SET_REFRESH_TOKEN_STATE',
    data
});

export const callRefreshToken = () => {
    return setRefreshTokenState({ refreshingToken: true, error: null });
};

export const logout = () => dispatch => {
    if (typeof window === 'undefined') {
        dispatch({ type: 'LOGOUT' });
    }
    else {
        Auth.logout();
    }
};

export const refreshTokenError = () => (dispatch, getState) => {
    const state = getState();

    if (!supportLocalStorage()
        || !window.localStorage.getItem('tokenExpire')
        || (state.user && state.user.tokenInfo && window.localStorage.getItem('tokenExpire') === state.user.tokenInfo.expire)) {
        dispatch(logout());
    }
    else {
        dispatch(setTokenExpire(window.localStorage.getItem('tokenExpire')));
    }

    return dispatch(setRefreshTokenState({ refreshingToken: false, error: true }));
};

export const refreshTokenSuccess = expire => {
    if (supportLocalStorage()) {
        window.localStorage.setItem('tokenExpire', `${ expire }`);
        window.localStorage.removeItem('refreshingToken');
    }
    return setRefreshTokenState({ refreshingToken: false, error: null, expire });
};

const legionAuthReturnAction = payload => ({
    type: 'API',
    actions: {
        received: (type, requestUrl, method, strParams, json) => (
            refreshTokenSuccess(jwtDecode(json.accessToken).exp * 1000) // eslint-disable-line no-magic-numbers
        ),
        receivedWithError: refreshTokenError
    },
    payload
});

export const refreshTokenWithLegion = () => {
    return legionAuthReturnAction({
        url: '/api/auth/refresh',
        method: 'POST'
    });
};

const migrateCoreSessionToLegion = () => {
    return legionAuthReturnAction({
        url: '/api/auth/session/migrate',
        method: 'POST',
        params: {
            grantType: 'migrate',
            type: 'refresh'
        }
    });
};

const sendAccountDetails = accountDetails => {
    const url = '/api/account';

    return {
        type: 'API',
        actions: {
            received: (type, requestUrl, method, strParams, json) => {
                const data = json.data;

                if (typeof window !== 'undefined') {
                    if (data && data.chat_token && window.localStorage) {
                        return Auth.setChatToken(data.chat_token, data.user_id);
                    }
                    else if (window.newrelic && accountDetails.grant_type !== 'refresh_token') {
                        window.newrelic.addPageAction('chat_login_error', { response: data, accountDetails });
                    }
                }
                if (data.expires_in) {
                    return refreshTokenSuccess(data.expires_in * 1000 + Date.now()); // eslint-disable-line no-magic-numbers
                }
                return null;
            },
            receivedWithError: refreshTokenError
        },
        payload: {
            url,
            method: 'POST',
            params: accountDetails,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json'
            }
        }
    };
};

const refreshTokenWithCore = dispatch => {
    return dispatch(sendAccountDetails({ grant_type: 'refresh_token' }));
};

export const refreshToken = withLegionSessionMigration => (dispatch, getState) => {
    const state = getState();

    dispatch(callRefreshToken());

    if (Auth.isLegionEnabled() || state.api.cookiesObject && state.api.cookiesObject.lf) {
        return dispatch(refreshTokenWithLegion());
    }

    if (withLegionSessionMigration && (state.api.cookiesObject && !state.api.cookiesObject.lf)) {
        return dispatch(migrateCoreSessionToLegion());
    }

    return dispatch(refreshTokenWithCore);
};

// session migration
export const setClassicToken = data => ({
    type: 'SET_CLASSIC_TOKEN',
    data
});

export const cleanClassicToken = () => ({
    type: 'CLEAN_CLASSIC_TOKEN'
});

const migrateSessionError = () => dispatch => {
    return dispatch(setRefreshTokenState({ error: true }));
};

const migrateSessionSuccess = expire => {
    return setRefreshTokenState({ error: null, expire });
};

const migrateSessionReturnAction = payload => ({
    type: 'API',
    actions: {
        received: (type, requestUrl, method, strParams, json) => {
            migrateSessionSuccess(jwtDecode(json.accessToken).exp * 1000); // eslint-disable-line no-magic-numbers
        },
        receivedWithError: migrateSessionError
    },
    payload
});

export const migrateClassicSession = (token, language = 'en', type) => {
    return migrateSessionReturnAction({
        url: '/api/auth/migrate',
        method: 'POST',
        params: {
            grantType: 'migrate',
            language,
            token,
            type
        }
    });
};
